import type { NativeSale } from '../../types/nativeSales';
import { formatMoney } from '../../utils/costFormatting';
import { describeDone } from '../../utils/posView';
import { Icon } from '../ui/Icon';
import { Button } from '../ui/primitives';
import { PosMethodTile } from './PosParts';

// Rediseño · Bloque D — «Venta completada» (P-028). Solo dibuja, y solo lo que el servidor devolvió
// de una venta que YA confirmó: esta pantalla nunca aparece antes del éxito real.
//
// Lo que la pantalla aprobada trae y aquí NO está: «Das de cambio» y «Recibido» (el producto no
// guarda cuánto se recibió); «Tarjeta · Zettle» y la píldora «Confirmado» (la venta no trae con qué
// se cobró la tarjeta: se dice «Tarjeta» y nada más); y «Se agregó al corte de Caja 1» (B0-14: no hay
// caja ni turno). Lo que la maqueta no dibuja y aquí SÍ está, porque ya existía y es real: el folio y
// «Ver detalle», que es el camino para anular la venta o pedir que se anule.

export function PosDone({ sale, onNewSale, onOpenDetail, firstSale }: {
  sale: NativeSale;
  onNewSale: () => void;
  /** Solo si la venta ya tiene su registro en el historial. */
  onOpenDetail: (() => void) | undefined;
  /**
   * ONB-001: esta es la PRIMERA venta completada del negocio (lo dijo el servidor). Agrega una línea breve
   * y el camino de vuelta al Inicio; todo lo demás de la pantalla queda igual.
   */
  firstSale?: { onGoHome: () => void };
}) {
  const view = describeDone(sale);
  return (
    <section aria-labelledby="pos-done-title" className="n3-pos n3-pos--done" tabIndex={-1}>
      <div className="n3-pos__stack">
        <div aria-live="polite" className="n3-pos__done" role="status">
          <span aria-hidden="true" className="n3-pos__done-check"><Icon name="check" size={38} /></span>
          <h1 className="n3-pos__done-title" id="pos-done-title">¡Venta completada!</h1>
          <p className="n3-pos__done-text">Todo quedó registrado.</p>
          {firstSale && (
            <p className="n3-pos__first">
              <strong className="n3-pos__first-title">Tu primera venta quedó registrada.</strong>
              <span className="n3-pos__first-text">Ya estás funcionando.</span>
            </p>
          )}
        </div>

        <div className="n3-pos__pay">
          <div className="n3-pos__pay-head">
            {view.method && <PosMethodTile method={view.method} />}
            <div className="n3-pos__pay-heading">
              <strong className="n3-pos__pay-title">{view.title}</strong>
              {view.methodLabel && <span className="n3-pos__pay-method">{view.methodLabel}</span>}
            </div>
          </div>
          <div className="n3-pos__pay-total">
            <p className="n3-pos__pay-label">Total</p>
            <p className="n3-pos__pay-amount">{formatMoney(sale.totalAmount)}</p>
            <p className="n3-pos__pill">{view.counts}</p>
          </div>
          {(sale.folio || sale.storeName) && (
            <dl className="n3-pos__pay-facts">
              {sale.folio && <div className="n3-pos__pay-fact"><dt className="n3-pos__pay-fact-label">Folio</dt><dd className="n3-pos__pay-fact-value">{sale.folio}</dd></div>}
              {sale.storeName && <div className="n3-pos__pay-fact"><dt className="n3-pos__pay-fact-label">Tienda</dt><dd className="n3-pos__pay-fact-value">{sale.storeName}</dd></div>}
            </dl>
          )}
        </div>

        <ul aria-label="Estado del registro" className="n3-pos__reg">
          <li className="n3-pos__reg-item"><span aria-hidden="true" className="n3-pos__reg-check"><Icon name="check" size={12} /></span>Venta registrada</li>
          <li className="n3-pos__reg-item"><span aria-hidden="true" className="n3-pos__reg-check"><Icon name="check" size={12} /></span>Pago registrado</li>
        </ul>
        <p className="n3-pos__done-note">No hay que cobrarla de nuevo.</p>
      </div>

      <div className="n3-pos__foot">
        <Button className="n3-pos__cta" fullWidth iconStart={<Icon name="plus" size={22} />} onClick={onNewSale} size="lg" variant="primary">Nueva venta</Button>
        {firstSale && <Button fullWidth iconStart={<Icon name="home" size={20} />} onClick={firstSale.onGoHome} variant="secondary">Ir a Inicio</Button>}
        {onOpenDetail && <Button fullWidth onClick={onOpenDetail} variant={firstSale ? 'ghost' : 'secondary'}>Ver detalle</Button>}
      </div>
    </section>
  );
}
